<template>
  <div style="display: flex; flex-direction: column; background: #F8F8F9;">
    <!-- Toolbar -->
    <div class="cm-toolbar">
      <h2>Discharge Waterfall</h2>

      <!-- Date stepper -->
      <div class="cm-stepper">
        <button class="cm-icon-btn" @click="shiftRange(-1)"><FontAwesomeIcon :icon="['fas', 'chevron-left']" style="font-size: 10px;" /></button>
        <input class="cm-input cm-input-sm" type="date" v-model="dateFrom" @change="fetchData" style="width: 108px; text-align: center;" />
        <span style="color: #A5A5A6; font-size: 11px;">→</span>
        <input class="cm-input cm-input-sm" type="date" v-model="dateTo" @change="fetchData" style="width: 108px; text-align: center;" />
        <button class="cm-icon-btn" @click="shiftRange(1)" :disabled="isToday"><FontAwesomeIcon :icon="['fas', 'chevron-right']" style="font-size: 10px;" /></button>
      </div>

      <!-- Quick range -->
      <div class="cm-seg">
        <button v-for="preset in rangePresets" :key="preset.label" :class="{ on: isActivePreset(preset) }" @click="applyPreset(preset)">{{ preset.label }}</button>
      </div>

      <span style="font-size: 11.5px; font-weight: 600;" :style="{ color: isToday ? '#1F9A63' : '#A5A5A6' }">{{ rangeLabel }}</span>

      <!-- Disposition filter -->
      <div style="margin-left: auto; display: flex; align-items: center; gap: 8px;">
        <div class="cm-seg">
          <button v-for="opt in dispositionOptions" :key="opt.value" :class="{ on: disposition === opt.value }" @click="setDisposition(opt.value)">{{ opt.label }}</button>
        </div>
        <button class="cm-icon-btn" @click="fetchData" :disabled="loading" title="Refresh">
          <FontAwesomeIcon :icon="['fas', 'arrows-rotate']" :spin="loading" style="font-size: 11px;" />
        </button>
      </div>
    </div>

    <!-- COHORT FILTERS. Region / Payer / Discharge type apply to the WHOLE page —
         the Sankey, every analysis panel and the episode list all read the same
         filter set server-side, so a number can never be filtered differently from
         the rows behind it. Region = Service Area, resolved from the patient's
         ATTRIBUTED TIN rather than the discharging hospital. -->
    <div v-if="!loading" style="display: flex; align-items: center; gap: 8px; padding: 0 16px 8px; flex-wrap: wrap;">
      <div style="min-width: 200px; max-width: 280px;">
        <SimpleMultiSelect
          v-model="serviceAreas"
          :options="serviceAreaOptions"
          :sensitive-labels="false"
          placeholder="All regions"
          :searchable="true"
          @change="onFilterChange"
        />
      </div>
      <div style="min-width: 180px; max-width: 260px;">
        <SimpleMultiSelect
          v-model="contracts"
          :options="contractOptions"
          :sensitive-labels="false"
          placeholder="All payers"
          :searchable="true"
          @change="onFilterChange"
        />
      </div>
      <div style="min-width: 170px; max-width: 240px;">
        <SimpleMultiSelect
          v-model="episodeTypes"
          :options="episodeTypeOptions"
          :sensitive-labels="false"
          placeholder="All discharge types"
          @change="onFilterChange"
        />
      </div>
      <button
        v-if="hasCohortFilters"
        style="font-size: 11px; color: #6B7280; text-decoration: underline; background: none; border: none; cursor: pointer;"
        @click="clearCohortFilters"
      >Clear</button>
      <span v-if="hasCohortFilters" style="font-size: 11px; color: #0369A1; font-weight: 600; font-variant-numeric: tabular-nums;">
        {{ (c.home_cohort || 0).toLocaleString() }} eligible discharges in this cohort
      </span>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="cm-loading"><div class="cm-spinner"></div></div>

    <template v-else-if="c.all_discharges > 0">
      <!-- SINGLE COLUMN, in reading order: the flow, then the episodes it resolves
           to, then the analysis of those episodes. -->
      <div style="padding: 8px 16px 16px; display: flex; flex-direction: column; gap: 14px;">
        <!-- SANKEY. It IS the reach funnel: every node is clickable and drills the
             episode list below, so the separate "Reach" step panel was removed
             rather than restating the same six numbers in a second form. A Sankey
             (not stacked bars) because this is a BRANCHING flow — SNF and hospice
             exit at disposition, and the reach split re-converges on outcome. -->
        <div class="cm-panel" style="padding: 16px;">
          <div style="display: flex; align-items: baseline; gap: 8px; margin-bottom: 4px;">
            <div style="font-size: 12px; font-weight: 700; color: #353537;">Discharge Flow</div>
            <div style="margin-left: auto; font-size: 10px; color: #A5A5A6;">click any node to list its episodes</div>
          </div>
          <div style="font-size: 10.5px; color: #A5A5A6; margin-bottom: 8px;">
            Community-discharge cohort → TOC enrolment → outreach timing → outcome.
            Scoped to TCM-eligible discharges: anchoring on every discharge made the
            dominant ribbon the ~89% never targeted, drowning out the outreach story.
          </div>
          <!-- Sankey_Diagram is position:absolute inset-0 and sizes off its
               container's clientWidth/Height, so it MUST have an explicitly
               sized position:relative wrapper. Without one it escapes to the
               nearest positioned ancestor and paints over the whole page. -->
          <div v-if="sankeyData" class="sankey-wrapper">
            <Sankey_Diagram :chart-data="sankeyData" :config="sankeyConfig"
                            @node-click="onSankeyNode" @link-click="onSankeyLink" />
          </div>
          <div v-else style="font-size: 11.5px; color: #A5A5A6; padding: 20px 0;">Not enough flow data to render.</div>
        </div>

        <!-- SANKEY AS A TABLE. The same links the diagram draws, in the form
             someone can paste into a deck or a mail. Copy emits TAB-separated
             text, which Excel and Sheets split into columns on paste (CSV would
             land in one column and need Text-to-Columns). Rows drill like every
             other statistic on the page. -->
        <div class="cm-panel" style="padding: 16px;">
          <div style="display: flex; align-items: baseline; gap: 8px; margin-bottom: 8px;">
            <div style="font-size: 12px; font-weight: 700; color: #353537;">Flow detail</div>
            <div style="font-size: 10.5px; color: #A5A5A6;">
              each row is one ribbon above; % is of the stage it flows from
            </div>
            <button class="wf-copy" style="margin-left: auto;" @click="copyFlowTable">
              <FontAwesomeIcon :icon="['fas', copied ? 'check' : 'copy']" style="font-size: 10px;" />
              {{ copied ? 'Copied' : 'Copy table' }}
            </button>
          </div>
          <table style="width: 100%; border-collapse: collapse;">
            <thead>
              <tr style="border-bottom: 1px solid #E8E9E9;">
                <th style="text-align: left;  padding: 5px 0;    font-size: 10.5px; font-weight: 600; color: #6B7280; width: 110px;">Stage</th>
                <th style="text-align: left;  padding: 5px 10px; font-size: 10.5px; font-weight: 600; color: #6B7280;">From</th>
                <th style="text-align: left;  padding: 5px 10px; font-size: 10.5px; font-weight: 600; color: #6B7280;">To</th>
                <th style="text-align: right; padding: 5px 10px; font-size: 10.5px; font-weight: 600; color: #6B7280; width: 100px;">Discharges</th>
                <th style="text-align: right; padding: 5px 0;    font-size: 10.5px; font-weight: 600; color: #6B7280; width: 70px;">% of from</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="r in flowTable"
                :key="r.from + '|' + r.to"
                :class="{ 'wf-clickable-row': !!r.segment, 'wf-active': r.segment && drillSegment === r.segment }"
                style="border-top: 1px solid #F0F0F1;"
                @click="r.segment && drill(r.segment, r.from + ' → ' + r.to)"
              >
                <td style="padding: 6px 0; font-size: 10.5px; color: #A5A5A6;">
                  <span v-if="r.firstOfStage">{{ r.stage }}</span>
                </td>
                <td style="padding: 6px 10px; font-size: 11.5px; color: #6B7280;">{{ r.from }}</td>
                <td style="padding: 6px 10px; font-size: 11.5px; color: #353537; font-weight: 500;">{{ r.to }}</td>
                <td style="padding: 6px 10px; text-align: right; font-size: 12px; font-weight: 600; font-variant-numeric: tabular-nums; color: #353537;">
                  {{ r.value.toLocaleString() }}
                </td>
                <td style="padding: 6px 0; text-align: right; font-size: 11.5px; font-variant-numeric: tabular-nums; color: #0284C7;">
                  {{ r.pct }}%
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- DID WE KNOW? The ADT precondition, as its own panel because a blended
             capture rate conflates two unrelated problems: outreach performance
             (we knew and did not work it) and feed coverage (we never knew). The
             two rates differ by ~20x for IHCI, so averaging them describes
             neither. -->
        <div class="cm-panel" style="padding: 16px;">
          <div style="display: flex; align-items: baseline; gap: 8px; margin-bottom: 2px;">
            <div style="font-size: 12px; font-weight: 700; color: #353537;">Did we know about the discharge?</div>
            <div style="margin-left: auto; font-size: 10px; color: #A5A5A6;">click a row to list its episodes</div>
          </div>
          <div style="font-size: 10.5px; color: #A5A5A6; margin-bottom: 12px;">
            How the discharge reached us. <strong>Found by staff</strong> means neither claims nor ADT
            ever produced it — it exists because a nurse enrolled the patient, and it is counted in the
            denominator like any other discharge. Capture rate is shown <em>within</em> each arm; the
            blended figure averages three unrelated situations and describes none of them.
          </div>
          <table style="width: 100%; border-collapse: collapse;">
            <thead>
              <tr style="border-bottom: 1px solid #E8E9E9;">
                <th style="text-align: left;  padding: 5px 0;    font-size: 10.5px; font-weight: 600; color: #6B7280;"></th>
                <th style="text-align: right; padding: 5px 10px; font-size: 10.5px; font-weight: 600; color: #6B7280; width: 110px;">Eligible</th>
                <th style="text-align: right; padding: 5px 10px; font-size: 10.5px; font-weight: 600; color: #6B7280; width: 110px;">Targeted</th>
                <th style="text-align: right; padding: 5px 0;    font-size: 10.5px; font-weight: 600; color: #6B7280; width: 90px;">Capture</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="a in adtRows" :key="a.key" class="wf-clickable-row"
                  :class="{ 'wf-active': drillSegment === a.segment }"
                  style="border-top: 1px solid #F0F0F1;"
                  @click="drill(a.segment, a.label)">
                <td style="padding: 8px 0; font-size: 11.5px; color: #353537; font-weight: 600;">
                  <span :style="{ color: a.colour }">●</span>
                  {{ a.label }}
                  <div style="font-size: 10px; color: #A5A5A6; font-weight: 400;">{{ a.note }}</div>
                </td>
                <td style="padding: 8px 10px; text-align: right; font-size: 12px; font-variant-numeric: tabular-nums; color: #353537;">
                  {{ a.eligible.toLocaleString() }}
                  <div style="font-size: 10px; color: #A5A5A6;">{{ pct(a.eligible, c.home_cohort) }}% of cohort</div>
                </td>
                <td style="padding: 8px 10px; text-align: right; font-size: 12px; font-variant-numeric: tabular-nums; color: #353537;">
                  {{ a.targeted.toLocaleString() }}
                </td>
                <td style="padding: 8px 0; text-align: right; font-size: 15px; font-weight: 700; font-variant-numeric: tabular-nums;"
                    :style="{ color: a.colour }">
                  {{ pct(a.targeted, a.eligible) }}%
                </td>
              </tr>
            </tbody>
          </table>
          <div style="margin-top: 10px; padding-top: 10px; border-top: 1px solid #F0F0F1; font-size: 10.5px; color: #6B7280;">
            <strong style="color: #353537;">Where the gap is:</strong>
            of {{ (c.src_claims || 0).toLocaleString() }} claims-sourced eligible discharges,
            {{ (c.src_claims_with_adt || 0).toLocaleString() }}
            ({{ pct(c.src_claims_with_adt, c.src_claims) }}%) had a matching ADT alert.
            The remainder were only ever visible in claims, which arrive too late to act on —
            except the {{ (c.found_by_staff || 0).toLocaleString() }} the team found without any
            feed telling them.
          </div>
        </div>

        <!-- EPISODE LIST. The rows behind whichever statistic is selected. `drill()`
             applies the SAME predicate the aggregate used, so this list always
             reconciles with the number that produced it. Defaults to every discharge
             in range so there is always a patient list on the page. -->
        <div class="cm-panel">
          <div style="padding: 12px 14px 10px; border-bottom: 1px solid #E8E9E9;">
            <div style="display: flex; align-items: baseline; gap: 8px;">
              <div style="font-size: 12px; font-weight: 700; color: #353537;">Episodes</div>
              <div style="margin-left: auto; display: flex; align-items: center; gap: 8px;">
                <span v-if="episodeTotal" style="font-size: 11px; color: #6B7280; font-variant-numeric: tabular-nums;">
                  {{ episodeTotal.toLocaleString() }}
                </span>
                <!-- Exports the CURRENT view — segment + cohort filters + column
                     filters — so the file cannot disagree with what is on screen. -->
                <button class="wf-copy" :disabled="exporting || !episodeTotal" @click="exportEpisodes('xlsx')">
                  <FontAwesomeIcon :icon="['fas', exporting ? 'spinner' : 'file-excel']" :spin="exporting" style="font-size: 10px;" />
                  {{ exporting ? 'Preparing…' : 'Excel' }}
                </button>
                <button class="wf-copy" :disabled="exporting || !episodeTotal" @click="exportEpisodes('csv')">
                  <FontAwesomeIcon :icon="['fas', 'file-lines']" style="font-size: 10px;" />
                  CSV
                </button>
              </div>
            </div>
            <div style="margin-top: 6px; display: flex; align-items: center; gap: 6px; flex-wrap: wrap;">
              <div class="wf-chip">
                {{ drillLabel }}
                <button v-if="drillSegment !== 'all'" class="wf-chip-x" title="Show all discharges" @click="clearDrill">
                  <FontAwesomeIcon :icon="['fas', 'xmark']" style="font-size: 9px;" />
                </button>
              </div>
              <!-- One chip per active column filter, so what is narrowing the list is
                   visible without opening each header dropdown. -->
              <div v-for="cf in activeColumnFilters" :key="cf.key" class="wf-chip">
                {{ cf.label }}: {{ cf.summary }}
                <button class="wf-chip-x" :title="'Clear ' + cf.label" @click="clearColumnFilter(cf.key)">
                  <FontAwesomeIcon :icon="['fas', 'xmark']" style="font-size: 9px;" />
                </button>
              </div>
              <span v-if="drillSegment === 'all' && !activeColumnFilters.length" style="font-size: 10px; color: #A5A5A6;">
                click any statistic to narrow, or filter a column below
              </span>
            </div>
          </div>

          <div v-if="episodesLoading" style="padding: 24px; text-align: center; color: #A5A5A6; font-size: 12px;">Loading…</div>

          <div v-else-if="episodes.length" class="wf-list-scroll">
            <table style="width: 100%; border-collapse: collapse;">
              <thead>
                <tr>
                  <th v-for="h in episodeHeaders" :key="h.k" class="wf-th"
                      :style="{ textAlign: h.n ? 'right' : 'left' }">
                    <!-- A filterable column's header IS its filter control. Keeps the
                         filter next to the data it acts on instead of in a toolbar
                         that has to name the column again. -->
                    <template v-if="h.f">
                      <button class="wf-colfilter" :class="{ on: (columnFilters[h.f] || []).length }"
                              @click.stop="toggleColumnMenu(h.f)">
                        {{ h.t }}
                        <FontAwesomeIcon :icon="['fas', 'filter']" style="font-size: 8px;" />
                        <span v-if="(columnFilters[h.f] || []).length">{{ (columnFilters[h.f] || []).length }}</span>
                      </button>
                      <div v-if="openColumnMenu === h.f" class="wf-colmenu" @click.stop>
                        <input v-model="columnMenuSearch" class="wf-colmenu-search" type="text" placeholder="Search…" />
                        <div class="wf-colmenu-list">
                          <label v-for="opt in filteredColumnOptions(h.f)" :key="opt.value" class="wf-colmenu-row">
                            <input type="checkbox" :checked="(columnFilters[h.f] || []).includes(opt.value)"
                                   @change="toggleColumnValue(h.f, opt.value)" />
                            <span class="wf-colmenu-label" :title="opt.value">{{ opt.value }}</span>
                            <span class="wf-colmenu-count">{{ opt.count.toLocaleString() }}</span>
                          </label>
                          <div v-if="!filteredColumnOptions(h.f).length" style="padding: 6px; color: #A5A5A6;">No matches</div>
                        </div>
                        <div class="wf-colmenu-foot">
                          <button @click="clearColumnFilter(h.f)">Clear</button>
                          <button @click="openColumnMenu = null">Done</button>
                        </div>
                      </div>
                    </template>
                    <template v-else>{{ h.t }}</template>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, i) in episodes" :key="row.bene_mbi_id + '|' + row.discharge_date + '|' + i"
                    :class="{ 'wf-clickable-row': !!row.entity_id }" @click="openEpisode(row)"
                    style="border-top: 1px solid #F0F0F1;">
                  <td class="wf-td">
                    <span class="sensitive" :style="{ color: row.entity_id ? '#0284C7' : '#353537' }">{{ row.patient_name || '—' }}</span>
                    <div class="sensitive" style="font-size: 10px; color: #A5A5A6;">{{ row.bene_mbi_id }}</div>
                  </td>
                  <td class="wf-td">{{ fmtDate(row.discharge_date) }}</td>
                  <td class="wf-td">{{ row.episode_type || '—' }}</td>
                  <td class="wf-td">{{ row.disposition || '—' }}</td>
                  <td class="wf-td wf-td-clip" :title="row.service_area || ''">{{ row.service_area || '—' }}</td>
                  <td class="sensitive wf-td" data-spoof-kind="contract">{{ row.contract_id || '—' }}</td>
                  <td class="wf-td" style="text-align: center;">
                    <span v-if="row.had_adt_alert" style="color: #0D9488; font-weight: 600;" title="ADT alert received for this discharge">Yes</span>
                    <span v-else style="color: #EA580C;" title="No ADT alert — this discharge was only visible in claims">No</span>
                  </td>
                  <td class="wf-td">
                    <span v-if="row.discharge_source === 'manual'" style="color: #7C3AED; font-weight: 600;"
                          title="In the data only because a nurse enrolled it — neither claims nor ADT produced this discharge">Staff</span>
                    <span v-else style="color: #6B7280;">{{ row.discharge_source || '—' }}</span>
                  </td>
                  <td class="wf-td wf-td-clip sensitive" :title="row.care_manager || ''">{{ row.care_manager || '—' }}</td>
                  <td class="wf-td" style="text-align: right; font-variant-numeric: tabular-nums;">
                    <span v-if="row.lace_score !== null && row.lace_score !== undefined">{{ row.lace_score }}</span>
                    <span v-else style="color:#A5A5A6;">—</span>
                  </td>
                  <td class="wf-td" style="text-align: center;">{{ loopMark(row.barrier_med) }}</td>
                  <td class="wf-td" style="text-align: center;">{{ loopMark(row.barrier_home) }}</td>
                  <td class="wf-td" style="text-align: center;">{{ loopMark(row.barrier_redflag) }}</td>
                  <td class="wf-td">
                    <span v-if="!row.barrier_surfaced" style="color:#A5A5A6;">—</span>
                    <span v-else-if="row.barrier_closed" style="color: #047857; font-weight: 600;">Resolved</span>
                    <span v-else style="color: #B45309; font-weight: 600;">Open</span>
                  </td>
                  <td class="wf-td">{{ ynDash(row.tcm_within_14) }}</td>
                  <td class="wf-td">
                    <span v-if="row.readmitted_30d" style="color: #B91C1C; font-weight: 600;">
                      Yes<span v-if="row.days_to_readmit !== null && row.days_to_readmit !== undefined"> · d{{ row.days_to_readmit }}</span>
                    </span>
                    <span v-else-if="!row.claims_mature" style="color:#A5A5A6;" title="Claims have not settled for this discharge yet">Pending</span>
                    <span v-else>No</span>
                  </td>
                  <td class="wf-td">{{ row.outcome || '—' }}</td>
                  <!-- The detailed outcome: the native era's terminal action name or
                       the migrated era's Care Source program status. One level under
                       the 6-value roll-up beside it. -->
                  <td class="wf-td wf-td-clip" :title="row.closure_reason || ''">{{ row.closure_reason || '—' }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div v-else style="padding: 24px; text-align: center; color: #A5A5A6; font-size: 12px;">No episodes in this selection.</div>

          <div v-if="episodeTotal > drillPageSize" style="display: flex; align-items: center; gap: 8px; padding: 8px 14px; border-top: 1px solid #E8E9E9;">
            <button class="cm-icon-btn" :disabled="drillOffset === 0" title="Previous" @click="pageEpisodes(-1)">
              <FontAwesomeIcon :icon="['fas', 'chevron-left']" style="font-size: 10px;" />
            </button>
            <button class="cm-icon-btn" :disabled="drillOffset + drillPageSize >= episodeTotal" title="Next" @click="pageEpisodes(1)">
              <FontAwesomeIcon :icon="['fas', 'chevron-right']" style="font-size: 10px;" />
            </button>
            <span style="font-size: 11px; color: #6B7280; font-variant-numeric: tabular-nums;">
              {{ (drillOffset + 1).toLocaleString() }}–{{ Math.min(drillOffset + drillPageSize, episodeTotal).toLocaleString() }}
              of {{ episodeTotal.toLocaleString() }}
            </span>
          </div>
        </div>

        <!-- BARRIER CLOSURE — throughput, outcome and the adjusted model in one
             panel. Previously three separate panels, which forced the reader to
             reconcile "392 resolved" (all discharges) against "248 resolved"
             (claims-mature only) with no explanation. Now the cohort narrowing
             is explicit: throughput row, then the mature subset the model is
             fit on. Bars removed — the number and % carry the information. -->
        <div v-if="c.graphed" class="cm-panel" style="padding: 16px;">
          <div style="font-size: 12px; font-weight: 700; color: #353537; margin-bottom: 2px;">Barrier closure</div>
          <div style="font-size: 10.5px; color: #A5A5A6; margin-bottom: 14px;">
            High-leverage barriers (med access, home services, red-flag routing) found in the care graph,
            driven to a documented terminal close — and whether closure tracks with readmission.
          </div>

          <!-- Throughput: of graphed discharges, what was found and closed. -->
          <div style="display: flex; align-items: stretch; flex-wrap: wrap; gap: 0; margin-bottom: 16px;">
            <template v-for="(stage, i) in barrierStages" :key="stage.label">
              <div v-if="i > 0" style="display: flex; align-items: center; padding: 0 2px; color: #D4D4D6;">
                <FontAwesomeIcon :icon="['fas', 'angle-right']" style="font-size: 11px;" />
              </div>
              <div class="wf-clickable" :class="{ 'wf-active': drillSegment === stage.segment }"
                   style="flex: 1 1 0; min-width: 150px; padding: 10px 12px; border: 1px solid #E8E9E9; border-radius: 6px;"
                   @click="drill(stage.segment, stage.label)">
                <div style="font-size: 10px; font-weight: 600; color: #6B7280; text-transform: uppercase; letter-spacing: .03em; margin-bottom: 5px;">
                  {{ stage.label }}
                </div>
                <div style="display: flex; align-items: baseline; gap: 6px;">
                  <span style="font-size: 19px; font-weight: 700; color: #353537; font-variant-numeric: tabular-nums; line-height: 1;">
                    {{ stage.value.toLocaleString() }}
                  </span>
                  <span v-if="i > 0" style="font-size: 12px; font-weight: 600; color: #7C3AED; font-variant-numeric: tabular-nums;">
                    {{ pct(stage.value, stage.base) }}%
                  </span>
                </div>
                <div style="font-size: 9.5px; color: #A5A5A6; margin-top: 5px; line-height: 1.3;">{{ stage.note }}</div>
              </div>
            </template>
          </div>

          <!-- Outcome + model, on the claims-mature subset. -->
          <div v-if="bm && !bm.insufficient" style="border-top: 1px solid #E8E9E9; padding-top: 12px;">
            <div style="font-size: 11px; font-weight: 600; color: #353537;">30-day readmission</div>
            <div style="font-size: 10px; color: #A5A5A6; margin-bottom: 10px;">
              Of the {{ bm.n.toLocaleString() }} barrier-surfaced discharges old enough for claims to have settled
              (&gt; 90 days) — {{ bm.n_closed.toLocaleString() }} resolved, {{ bm.n_open.toLocaleString() }} open.
              Adjusted for follow-up, follow-up×TCM, LACE and HCC; cluster-robust SE by patient.
            </div>

            <table style="width: 100%; border-collapse: collapse;">
              <thead>
                <tr style="border-bottom: 1px solid #E8E9E9;">
                  <th style="text-align: left;  padding: 5px 0;    font-size: 10.5px; font-weight: 600; color: #6B7280;"></th>
                  <th style="text-align: right; padding: 5px 12px; font-size: 10.5px; font-weight: 600; color: #6B7280; width: 100px;">Discharges</th>
                  <th style="text-align: right; padding: 5px 12px; font-size: 10.5px; font-weight: 600; color: #6B7280; width: 90px;">Readmits</th>
                  <th style="text-align: right; padding: 5px 12px; font-size: 10.5px; font-weight: 600; color: #6B7280; width: 90px;">Observed</th>
                  <th style="text-align: right; padding: 5px 0;    font-size: 10.5px; font-weight: 600; color: #6B7280; width: 90px;">Adjusted</th>
                </tr>
              </thead>
              <tbody>
                <tr class="wf-clickable-row" :class="{ 'wf-active': drillSegment === 'model_closed' }" @click="drill('model_closed','Barrier resolved (claims-mature)')" style="border-top: 1px solid #F0F0F1;">
                  <td style="padding: 8px 0; font-size: 11.5px; color: #353537;">Barrier resolved</td>
                  <td style="padding: 8px 12px; text-align: right; font-size: 12px; font-variant-numeric: tabular-nums;">{{ bm.n_closed.toLocaleString() }}</td>
                  <td style="padding: 8px 12px; text-align: right; font-size: 12px; font-variant-numeric: tabular-nums;">{{ closedReadmits }}</td>
                  <td style="padding: 8px 12px; text-align: right; font-size: 12px; font-variant-numeric: tabular-nums; color: #6B7280;">{{ bm.raw_closed_rate }}%</td>
                  <td style="padding: 8px 0;  text-align: right; font-size: 13px; font-weight: 700; font-variant-numeric: tabular-nums; color: #0E6B43;">{{ bm.adj_closed_rate }}%</td>
                </tr>
                <tr class="wf-clickable-row" :class="{ 'wf-active': drillSegment === 'model_open' }" @click="drill('model_open','Barrier still open (claims-mature)')" style="border-top: 1px solid #F0F0F1;">
                  <td style="padding: 8px 0; font-size: 11.5px; color: #353537;">Barrier still open</td>
                  <td style="padding: 8px 12px; text-align: right; font-size: 12px; font-variant-numeric: tabular-nums;">{{ bm.n_open.toLocaleString() }}</td>
                  <td style="padding: 8px 12px; text-align: right; font-size: 12px; font-variant-numeric: tabular-nums;">{{ openReadmits }}</td>
                  <td style="padding: 8px 12px; text-align: right; font-size: 12px; font-variant-numeric: tabular-nums; color: #6B7280;">{{ bm.raw_open_rate }}%</td>
                  <td style="padding: 8px 0;  text-align: right; font-size: 13px; font-weight: 700; font-variant-numeric: tabular-nums; color: #B91C1C;">{{ bm.adj_open_rate }}%</td>
                </tr>
              </tbody>
            </table>

            <!-- Effect + extrapolation. -->
            <div style="display: flex; flex-wrap: wrap; gap: 0; margin-top: 14px;">
              <div style="flex: 1 1 0; min-width: 150px; padding: 10px 12px; border: 1px solid #E8E9E9; border-radius: 6px;">
                <div style="font-size: 10px; font-weight: 600; color: #6B7280; text-transform: uppercase; letter-spacing: .03em; margin-bottom: 5px;">Odds ratio</div>
                <div style="font-size: 19px; font-weight: 700; color: #353537; font-variant-numeric: tabular-nums; line-height: 1;">{{ bm.odds_ratio }}</div>
                <div style="font-size: 9.5px; color: #A5A5A6; margin-top: 5px;">95% CI {{ bm.ci_low }}–{{ bm.ci_high }} · p {{ fmtP(bm.p_value) }}</div>
              </div>
              <div style="width: 8px;"></div>
              <div style="flex: 1 1 0; min-width: 150px; padding: 10px 12px; border: 1px solid #E8E9E9; border-radius: 6px;">
                <div style="font-size: 10px; font-weight: 600; color: #6B7280; text-transform: uppercase; letter-spacing: .03em; margin-bottom: 5px;">Risk difference</div>
                <div style="font-size: 19px; font-weight: 700; color: #353537; font-variant-numeric: tabular-nums; line-height: 1;">{{ bm.risk_diff_pts }} pts</div>
                <div style="font-size: 9.5px; color: #A5A5A6; margin-top: 5px;">adjusted, resolved vs open</div>
              </div>
              <div style="width: 8px;"></div>
              <div style="flex: 1 1 0; min-width: 150px; padding: 10px 12px; border: 1px solid #E8E9E9; border-radius: 6px;">
                <div style="font-size: 10px; font-weight: 600; color: #6B7280; text-transform: uppercase; letter-spacing: .03em; margin-bottom: 5px;">Readmissions avoided</div>
                <div style="font-size: 19px; font-weight: 700; color: #353537; font-variant-numeric: tabular-nums; line-height: 1;">{{ Math.round(bm.avoided_readmissions).toLocaleString() }}</div>
                <div style="font-size: 9.5px; color: #A5A5A6; margin-top: 5px;">{{ bm.risk_diff_pts }} pts × {{ bm.n_closed.toLocaleString() }} resolved</div>
              </div>
              <div style="width: 8px;"></div>
              <div style="flex: 1 1 0; min-width: 150px; padding: 10px 12px; border: 1px solid #E8E9E9; border-radius: 6px;">
                <div style="font-size: 10px; font-weight: 600; color: #6B7280; text-transform: uppercase; letter-spacing: .03em; margin-bottom: 5px;">Cost avoided</div>
                <div style="font-size: 19px; font-weight: 700; color: #353537; font-variant-numeric: tabular-nums; line-height: 1;">${{ bm.avoided_cost.toLocaleString() }}</div>
                <div style="font-size: 9.5px; color: #A5A5A6; margin-top: 5px;">at ${{ (bm.unit_cost / 1000) }}k per readmission</div>
              </div>
            </div>

            <div style="display: flex; gap: 18px; flex-wrap: wrap; margin-top: 10px; font-size: 10.5px; color: #6B7280;">
              <span v-for="cv in bm.covariates" :key="cv.term">
                {{ cv.term }} <strong style="color: #353537;">{{ cv.odds_ratio }}</strong>
                <span :style="{ color: cv.p_value < 0.05 ? '#B91C1C' : '#A5A5A6' }">p {{ fmtP(cv.p_value) }}</span>
              </span>
            </div>

            <div style="font-size: 10px; color: #A5A5A6; margin-top: 8px;">
              Avoided events use the <em>adjusted</em> risk difference (g-computation over the fitted model), not the
              unadjusted gap, so the comorbidity imbalance between arms is removed.
              ${{ (bm.unit_cost / 1000) }}k is a planning assumption, not measured spend.
              Associational: closure is not randomly assigned.
            </div>
          </div>
          <div v-else-if="bm && bm.insufficient" style="border-top: 1px solid #E8E9E9; padding-top: 12px; font-size: 11px; color: #A5A5A6;">
            Not enough claims-mature barrier-surfaced discharges in this range to fit the model
            ({{ bm.n_closed }} resolved / {{ bm.n_open }} open).
          </div>
        </div>

        <!-- Two levers: TCM billing x barrier closure. -->
        <div v-if="ladder" class="cm-panel" style="padding: 16px;">
          <div style="font-size: 12px; font-weight: 700; color: #353537;">Two levers: TCM billing and barrier closure</div>
          <div style="font-size: 10.5px; color: #A5A5A6; margin-bottom: 12px;">
            Adjusted 30-day readmission for each combination, holding comorbidity constant.
            The two act independently — the interaction term is null
            (OR {{ ladder.interaction_odds_ratio }}, p {{ fmtP(ladder.interaction_p_value) }}),
            so the reductions add rather than overlap.
          </div>

          <table style="width: 100%; border-collapse: collapse;">
            <thead>
              <tr style="border-bottom: 1px solid #E8E9E9;">
                <th style="text-align: left;  padding: 5px 0;    font-size: 10.5px; font-weight: 600; color: #6B7280;">Combination</th>
                <th style="text-align: right; padding: 5px 10px; font-size: 10.5px; font-weight: 600; color: #6B7280; width: 80px;">Adjusted</th>
                <th style="text-align: right; padding: 5px 10px; font-size: 10.5px; font-weight: 600; color: #6B7280; width: 110px;">Observed</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="r in ladderRows" :key="r.label" class="wf-clickable-row" :class="{ 'wf-active': drillSegment === r.segment }" @click="drill(r.segment, r.label)" style="border-top: 1px solid #F0F0F1;">
                <td style="padding: 7px 0; font-size: 11.5px;" :style="{ color: r.neither || r.both ? '#353537' : '#6B7280', fontWeight: (r.neither || r.both) ? 600 : 400 }">
                  {{ r.label }}
                </td>
                <td style="padding: 7px 10px; text-align: right; font-size: 13px; font-weight: 700; font-variant-numeric: tabular-nums; color: #353537;">{{ r.adj }}%</td>
                <td style="padding: 7px 10px; text-align: right; font-size: 11px; font-variant-numeric: tabular-nums; color: #6B7280;">
                  <span v-if="r.o">{{ r.o.rate }}% <span style="color:#A5A5A6;">({{ r.o.readmit }}/{{ r.o.n }})</span></span>
                </td>
              </tr>
            </tbody>
          </table>

          <div style="display: flex; gap: 18px; flex-wrap: wrap; margin-top: 10px; font-size: 10.5px; color: #6B7280;">
            <span>TCM <strong style="color:#353537;">OR {{ ladder.tcm_odds_ratio }}</strong> p {{ fmtP(ladder.tcm_p_value) }}</span>
            <span>Barrier closure <strong style="color:#353537;">OR {{ ladder.barrier_odds_ratio }}</strong> p {{ fmtP(ladder.barrier_p_value) }}</span>
          </div>

          <div style="border-top: 1px solid #E8E9E9; margin-top: 12px; padding-top: 10px;">
            <div style="font-size: 11px; font-weight: 600; color: #353537; margin-bottom: 6px;">TCM lever — delivered vs unrealised</div>
            <table style="width: 100%; border-collapse: collapse;">
              <tbody>
                <tr>
                  <td style="padding: 4px 0; font-size: 11.5px; color: #6B7280;">
                    {{ ladder.n_tcm.toLocaleString() }} discharges with TCM billed
                    ({{ ladder.tcm_risk_diff_pts }} pt adjusted reduction)
                  </td>
                  <td style="padding: 4px 0; text-align: right; font-size: 12px; font-weight: 600; font-variant-numeric: tabular-nums; color: #353537;">
                    {{ Math.round(ladder.tcm_avoided) }} avoided · ${{ ladder.tcm_avoided_cost.toLocaleString() }}
                  </td>
                </tr>
                <tr>
                  <td style="padding: 4px 0; font-size: 11.5px; color: #B45309;">
                    {{ ladder.n_no_tcm.toLocaleString() }} without TCM — if they had received it
                  </td>
                  <td style="padding: 4px 0; text-align: right; font-size: 12px; font-weight: 700; font-variant-numeric: tabular-nums; color: #B45309;">
                    {{ Math.round(ladder.tcm_opportunity) }} avoidable · ${{ ladder.tcm_opportunity_cost.toLocaleString() }}
                  </td>
                </tr>
              </tbody>
            </table>
            <div style="font-size: 10px; color: #A5A5A6; margin-top: 8px;">
              TCM billing partly marks an engaged, reachable patient, so some of this is selection rather than effect.
              Associational; ${{ (bm.unit_cost / 1000) }}k per readmission is a planning assumption.
            </div>
          </div>
        </div>

        <!-- 30-day readmission by outreach band. -->
        <div class="cm-panel" style="padding: 16px;">
          <div style="font-size: 12px; font-weight: 700; color: #353537;">30-day readmission by outreach band</div>
          <div style="font-size: 10.5px; color: #A5A5A6; margin-bottom: 10px;">
            Mature claims only (discharged &gt; 90 days ago). Targeting is risk-based, so the comparable
            contrast is <em>within</em> the targeted population; the not-targeted row is context, not a control.
          </div>
          <table style="width: 100%; border-collapse: collapse;">
            <thead>
              <tr style="border-bottom: 1px solid #E8E9E9;">
                <th style="text-align: left;  padding: 5px 0;    font-size: 10.5px; font-weight: 600; color: #6B7280;">Band</th>
                <th style="text-align: right; padding: 5px 10px; font-size: 10.5px; font-weight: 600; color: #6B7280; width: 90px;">Discharges</th>
                <th style="text-align: right; padding: 5px 10px; font-size: 10.5px; font-weight: 600; color: #6B7280; width: 90px;">Readmits</th>
                <th style="text-align: right; padding: 5px 10px; font-size: 10.5px; font-weight: 600; color: #6B7280; width: 64px;">Rate</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="b in readmitBands" :key="b.label" style="border-top: 1px solid #F0F0F1;">
                <td style="padding: 7px 0; font-size: 11.5px; color: #353537;">
                  {{ b.label }}
                  <div style="font-size: 10px; color: #A5A5A6;">{{ b.note }}</div>
                </td>
                <td style="padding: 7px 10px; text-align: right; font-size: 12px; font-variant-numeric: tabular-nums;">{{ b.n.toLocaleString() }}</td>
                <td style="padding: 7px 10px; text-align: right; font-size: 12px; font-variant-numeric: tabular-nums;">{{ b.readmit.toLocaleString() }}</td>
                <td style="padding: 7px 10px; text-align: right; font-size: 12px; font-variant-numeric: tabular-nums; font-weight: 600; color: #353537;">
                  <span v-if="b.pct !== null">{{ b.pct }}%</span><span v-else style="color:#A5A5A6;">—</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div><!-- /column -->

    </template>

    <div v-else style="padding: 40px; text-align: center; color: #A5A5A6; font-size: 12px;">No discharges in the selected range.</div>

    <!-- Patient detail for a clicked episode. -->
    <div v-if="selectedEpisode" style="position: fixed; inset: 0; z-index: 60; display: flex; justify-content: flex-end; background: rgba(38,38,40,.28);"
         @click.self="selectedEpisode = null">
      <div style="width: 60%; max-width: 1100px; background: #FFFFFF; height: 100%; overflow: auto; box-shadow: -4px 0 24px rgba(38,38,40,.18);">
        <Patient_Drawer
          :patient-identifier="selectedEpisode.bene_mbi_id"
          :entity-id="selectedEpisode.entity_id"
          :patient-name="selectedEpisode.patient_name"
          :enabled-tabs="['details','programs','engagement','care_graph','utilization']"
          :show-header="true"
          default-tab="care_graph"
          metric-category="Care Management"
          @close="selectedEpisode = null"
        />
      </div>
    </div>
  </div>
</template>

<script>
import Sankey_Diagram from '@/components/widgetcomponents/Viz_Elements/Sankey_Diagram.vue';
import Patient_Drawer from '@/components/Patient_Drawer/Patient_Drawer.vue';
import SimpleMultiSelect from '@/components/widgetcomponents/Dropdown_Selector/SimpleMultiSelect.vue';
import { startDischargeWaterfallExport } from '@/services/api/exportsApi';
import { showErrorToast } from '@/utils/loadError';

// Sankey node name -> episode-list segment. The names are produced by the
// service's enrol_node / reach_node / outcome_node CASE expressions; the segments
// are keys of DischargeWaterfallService.SEGMENTS, i.e. the SAME predicate the
// aggregate used — which is what guarantees the list reconciles with the ribbon.
const SANKEY_NODE_SEGMENT = {
  'Eligible': 'eligible',
  'ADT received': 'had_adt',
  'Found by staff': 'found_by_staff',
  'No ADT': 'no_adt_no_manual',
  'Targeted': 'targeted',
  'Not targeted': 'not_targeted',
  'Reached <48h': 'reached_48h',
  'Reached later': 'reached_later',
  'Not reached': 'not_reached',
  'Succeeded': 'succeeded',
  'Unsuccessful': 'unsuccessful',
  'Not engaged': 'not_engaged',
  'Still open': 'outcome_open',
  'Other outcome': 'outcome_other',
};

// Links whose intersection is itself a named segment. Everything else falls back
// to the target node (a superset of the ribbon) rather than inventing a predicate.
const SANKEY_LINK_SEGMENT = {
  'Eligible|ADT received': 'had_adt',
  'Eligible|Found by staff': 'found_by_staff',
  'Eligible|No ADT': 'no_adt_no_manual',
  // The four ADT x targeted cells. 'ADT received -> Not targeted' is the one to
  // look at: we knew about the discharge and still did not work it.
  'ADT received|Targeted': 'had_adt_targeted',
  'ADT received|Not targeted': 'had_adt_missed',
  'No ADT|Targeted': 'no_adt_targeted',
  'No ADT|Not targeted': 'no_adt_missed',
  'Targeted|Reached <48h': 'reached_48h',
  'Targeted|Reached later': 'reached_later',
  'Targeted|Not reached': 'not_reached',
};

export default {
  name: 'CareManagement_DischargeWaterfall',
  components: { Sankey_Diagram, Patient_Drawer, SimpleMultiSelect },
  data() {
    const now = new Date();
    const fmt = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    // Default range: year-to-date (Jan 1 of the current year through today).
    const start = new Date(now.getFullYear(), 0, 1);
    return {
      loading: false,
      result: { counts: {}, readmit_histogram: [] },
      dateFrom: fmt(start),
      dateTo: fmt(now),
      disposition: null,
      // Cohort filters. Arrays because a user compares two regions or two payers
      // more often than they look at exactly one.
      serviceAreas: [],
      contracts: [],
      episodeTypes: [],
      filterOptions: { service_area: [], contract: [], episode_type: [] },
      copied: false,
      // Per-column filters on the episode list. SEPARATE from the cohort filters
      // above: these narrow the LIST beneath a statistic without changing the
      // statistic, so the aggregates stay computed on the full cohort.
      columnFilters: {},
      episodeFilterOptions: {},
      openColumnMenu: null,
      columnMenuSearch: '',
      // The export runs as a background job; the Downloads menu and a toast deliver the file.
      startingExport: false,
      exportJobId: null,
      // episode drilldown
      // The episode list is ALWAYS populated — it defaults to every discharge in
      // range, and clicking a statistic narrows it. A page whose patient list
      // only appears after a click reads as if there is no list at all.
      drillSegment: 'all',
      drillLabel: 'All discharges',
      episodes: [],
      episodeTotal: 0,
      episodesLoading: false,
      drillOffset: 0,
      drillPageSize: 100,
      selectedEpisode: null,
      dispositionOptions: [
        { value: null, label: 'All' },
        { value: 'HOME', label: 'Home' },
        { value: 'HOME_HEALTH', label: 'Home + HH' },
      ],
    };
  },
  computed: {
    exporting() {
      if (this.startingExport) return true;
      const job = this.exportJobId && this.$store.getters['exports/job'](this.exportJobId);
      return !!job && (job.status === 'pending' || job.status === 'in_progress');
    },
    c() { return this.result.counts || {}; },
    bm() { return this.result.barrier_model || null; },
    ladder() { return this.bm && this.bm.ladder ? this.bm.ladder : null; },
    closedReadmits() { return this.c.bar_closed_readmit || 0; },
    openReadmits() { return this.c.bar_open_readmit || 0; },
    ladderRows() {
      const l = this.ladder;
      if (!l) return [];
      const obs = {};
      (l.observed || []).forEach(o => { obs[`${o.tcm}|${o.closed}`] = o; });
      const segOf = (tcm, closed) => closed
        ? (tcm ? 'cell_closed_tcm' : 'cell_closed_notcm')
        : (tcm ? 'cell_open_tcm' : 'cell_open_notcm');
      return (l.cells || []).map(c => ({
        segment: segOf(c.tcm, c.closed),
        label: (c.closed ? 'Barrier resolved' : 'Barrier open') + ' · ' + (c.tcm ? 'TCM billed' : 'no TCM'),
        both: c.tcm && c.closed,
        neither: !c.tcm && !c.closed,
        adj: c.adj_rate,
        o: obs[`${c.tcm}|${c.closed}`] || null,
      }));
    },
    // The ADT precondition arms. Ordered ADT-first because that is the arm the
    // team controls; the No-ADT arm is a data-coverage problem, not an outreach one.
    adtRows() {
      const c = this.c;
      return [
        {
          key: 'had', segment: 'had_adt', label: 'ADT alert received',
          note: 'the feed told us, in time to act',
          eligible: c.had_adt || 0, targeted: c.had_adt_targeted || 0, colour: '#0D9488',
        },
        {
          key: 'staff', segment: 'found_by_staff', label: 'Found by staff',
          note: 'no claim, no ADT — a nurse enrolled it anyway',
          eligible: c.found_by_staff || 0, targeted: c.found_by_staff_targeted || 0, colour: '#7C3AED',
        },
        {
          key: 'none', segment: 'no_adt_no_manual', label: 'No ADT alert',
          note: 'only visible in claims, which land ~2 months later',
          eligible: c.no_adt_no_manual || 0, targeted: c.no_adt_no_manual_targeted || 0, colour: '#EA580C',
        },
      ];
    },
    // The Sankey's links as rows. Ordered by stage so the table reads down the flow
    // rather than in whatever order the service emitted the links, and each row's %
    // is of ITS SOURCE node — the same "share of the stage above" the funnel used,
    // not a share of the whole cohort.
    flowTable() {
      const links = this.result.sankey_links || [];
      if (!links.length) return [];
      const STAGE = {
        'Eligible': 'ADT',
        'ADT received': 'Enrolment',
        'Found by staff': 'Enrolment',
        'No ADT': 'Enrolment',
        'Targeted': 'Outreach',
        'Not targeted': 'Outreach',
        'Reached <48h': 'Outcome',
        'Reached later': 'Outcome',
        'Not reached': 'Outcome',
      };
      const ORDER = ['ADT', 'Enrolment', 'Outreach', 'Outcome'];
      const NODE_ORDER = [
        'Eligible', 'ADT received', 'Found by staff', 'No ADT', 'Targeted', 'Not targeted',
        'Reached <48h', 'Reached later', 'Not reached',
        'Succeeded', 'Unsuccessful', 'Not engaged', 'Still open', 'Other outcome',
      ];
      // Source totals, so % is of the ribbon's own origin node.
      const srcTotal = {};
      links.forEach(l => { srcTotal[l.source] = (srcTotal[l.source] || 0) + l.value; });
      const rows = links
        .filter(l => l.value > 0)
        .map(l => ({
          stage: STAGE[l.source] || 'Flow',
          from: l.source,
          to: l.target,
          value: l.value,
          pct: srcTotal[l.source] ? Math.round((l.value / srcTotal[l.source]) * 100) : 0,
          segment: SANKEY_LINK_SEGMENT[`${l.source}|${l.target}`] || SANKEY_NODE_SEGMENT[l.target] || null,
        }))
        .sort((a, b) => {
          const s = ORDER.indexOf(a.stage) - ORDER.indexOf(b.stage);
          if (s !== 0) return s;
          const f = NODE_ORDER.indexOf(a.from) - NODE_ORDER.indexOf(b.from);
          if (f !== 0) return f;
          return b.value - a.value;
        });
      // Only label the stage on its first row, so the column reads as a grouping.
      let last = null;
      rows.forEach(r => { r.firstOfStage = r.stage !== last; last = r.stage; });
      return rows;
    },
    // Options carry their row count so a user can see a region is thin before
    // filtering to it and finding four discharges.
    serviceAreaOptions() {
      return (this.filterOptions.service_area || [])
        .map(o => ({ value: o.value, label: `${o.value} (${o.count.toLocaleString()})` }));
    },
    contractOptions() {
      return (this.filterOptions.contract || [])
        .map(o => ({ value: o.value, label: `${o.value} (${o.count.toLocaleString()})` }));
    },
    episodeTypeOptions() {
      const NICE = { ED_VISIT: 'ED visit', INPATIENT: 'Inpatient', OBSERVATION_STAY: 'Observation' };
      return (this.filterOptions.episode_type || [])
        .map(o => ({ value: o.value, label: `${NICE[o.value] || o.value} (${o.count.toLocaleString()})` }));
    },
    activeColumnFilters() {
      const LABEL = {
        episode_type: 'Type', disposition: 'Disposition', service_area: 'Region',
        contract_id: 'Payer', care_manager: 'Care manager', outcome: 'Outcome',
        had_adt_alert: 'ADT', discharge_source: 'Found via',
        closure_reason: 'Detailed outcome', program: 'Program',
      };
      return Object.entries(this.columnFilters)
        .filter(([, v]) => v && v.length)
        .map(([k, v]) => ({
          key: k,
          label: LABEL[k] || k,
          summary: v.length === 1 ? v[0] : `${v.length} selected`,
        }));
    },
    hasCohortFilters() {
      return this.serviceAreas.length > 0 || this.contracts.length > 0 || this.episodeTypes.length > 0;
    },
    // Sent with every request — aggregates AND episode list — so the two cannot
    // be looking at different cohorts.
    cohortParams() {
      return {
        service_areas: this.serviceAreas.length ? this.serviceAreas : null,
        contracts: this.contracts.length ? this.contracts : null,
        episode_types: this.episodeTypes.length ? this.episodeTypes : null,
      };
    },
    // `f` names the server-side filter column, which also marks the header as a
    // filter control. Columns without it are derived or free-text and are not
    // usefully filterable.
    episodeHeaders() {
      return [
        {k:'pt',t:'Patient'},
        {k:'dt',t:'Discharge'},
        {k:'ep',t:'Type',        f:'episode_type'},
        {k:'dp',t:'Disposition', f:'disposition'},
        {k:'sa',t:'Region',      f:'service_area'},
        {k:'py',t:'Payer',       f:'contract_id'},
        {k:'adt',t:'ADT',        f:'had_adt_alert'},
        {k:'src',t:'Found via',  f:'discharge_source'},
        {k:'cm',t:'Care Manager',f:'care_manager'},
        {k:'lc',t:'LACE',n:true},
        {k:'md',t:'Med'},{k:'hm',t:'Home'},{k:'rf',t:'Flag'},{k:'bc',t:'Barrier'},
        {k:'tc',t:'TCM'},{k:'rd',t:'Readmit'},
        {k:'oc',t:'Outcome',     f:'outcome'},
        {k:'dor',t:'Detailed Outcome', f:'closure_reason'},
      ];
    },

    // Claims (TCM / readmit) land ~60 days behind discharge. When most of the
    // selected range sits inside that window the stages read 0 for a data-timing
    // reason, not a care reason — show "—" + an explanation instead of a bare 0.
    claimsImmature() { return this.c.claims_immature_discharges || 0; },
    claimsPending() {
      const all = this.c.all_discharges || 0;
      return all > 0 && this.claimsImmature / all >= 0.8;
    },
    isToday() { const n = new Date(); return this.dateTo === `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`; },
    rangeLabel() {
      const days = Math.round((new Date(this.dateTo + 'T12:00:00') - new Date(this.dateFrom + 'T12:00:00')) / 86400000) + 1;
      return `${days} days`;
    },
    rangePresets() {
      const now = new Date();
      const fmt = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      const daysAgo = n => { const d = new Date(now); d.setDate(d.getDate() - n); return fmt(d); };
      return [
        { label: '7d', from: daysAgo(6), to: fmt(now) },
        { label: '30d', from: daysAgo(29), to: fmt(now) },
        { label: '90d', from: daysAgo(89), to: fmt(now) },
        { label: 'YTD', from: fmt(new Date(now.getFullYear(), 0, 1)), to: fmt(now) },
      ];
    },
    // Care-graph barrier throughput. Kept as its OWN block rather than folded into
    // the Sankey, because its denominator is "graphed" rather than the stage above —
    // barriers are only detectable where a care graph exists.
    barrierStages() {
      const c = this.c;
      const graphed = c.graphed || 0;
      const surfaced = c.barrier_surfaced || 0;
      return [
        { label: 'Care graph parsed', value: graphed, base: graphed, note: 'signals extracted from the record', segment: 'graphed' },
        { label: 'High-leverage barrier surfaced', value: surfaced, base: graphed, note: 'med access, home services or red-flag routing', segment: 'barrier_surfaced' },
        { label: 'Barrier resolved (verified)', value: c.barrier_closed || 0, base: surfaced, note: 'terminal act documented, not just referred', segment: 'barrier_closed' },
      ];
    },
    // Readmission within 30 days by outreach band. Mature claims only.
    readmitBands() {
      const c = this.c;
      const band = (label, n, r, note) => ({
        label, n: n || 0, readmit: r || 0,
        pct: n ? Math.round((r / n) * 1000) / 10 : null, note,
      });
      return [
        band('Succeeded', c.mature_succeeded, c.mature_succeeded_readmit, 'targeted and closed successfully'),
        band('Targeted, not succeeded', c.mature_notsucceeded, c.mature_notsucceeded_readmit, 'targeted but not closed successfully'),
        band('Not targeted', c.mature_nottargeted, c.mature_nottargeted_readmit, 'no opportunity created'),
      ];
    },
    // 30-day readmission split by barrier closure, within the barrier-surfaced
    // cohort — both arms had an actionable barrier found, so the contrast is
    // like-for-like.
    barrierReadmitBands() {
      const c = this.c;
      const band = (label, n, r, note) => ({
        label, n: n || 0, readmit: r || 0,
        pct: n ? Math.round((r / n) * 1000) / 10 : null, note,
      });
      return [
        band('Barrier resolved', c.bar_closed_n, c.bar_closed_readmit, 'terminal act documented'),
        band('Barrier still open', c.bar_open_n, c.bar_open_readmit, 'no verified closure'),
      ];
    },
    maxBarrierPct() { return Math.max(1, ...this.barrierReadmitBands.map(b => b.pct || 0)); },
    sankeyData() {
      const links = this.result.sankey_links || [];
      if (!links.length) return null;
      // 'Eligible' is the ROOT node the service emits (the TCM-eligible cohort).
      // It was missing from both maps, so every link out of it was filtered away
      // and the chart silently lost its entire first stage.
      const COLORS = {
        'Discharged': '#475569', 'Eligible': '#475569',
        'ADT received': '#0D9488', 'Found by staff': '#7C3AED', 'No ADT': '#F97316',
        'Home': '#0EA5E9', 'Home Health': '#38BDF8', 'SNF': '#8B5CF6',
        'Hospice': '#94A3B8', 'Other': '#CBD5E1',
        'Targeted': '#0284C7', 'Not targeted': '#F59E0B',
        'Reached <48h': '#1F9A63', 'Reached later': '#86EFAC', 'Not reached': '#F87171',
        'Succeeded': '#059669', 'Unsuccessful': '#D97706', 'Not engaged': '#DC2626',
        'Still open': '#94A3B8', 'Other outcome': '#CBD5E1',
      };
      const ORDER = [
        'Discharged', 'Eligible',
        'ADT received', 'Found by staff', 'No ADT',
        'Home', 'Home Health', 'SNF', 'Hospice', 'Other',
        'Targeted', 'Not targeted',
        'Reached <48h', 'Reached later', 'Not reached',
        'Succeeded', 'Unsuccessful', 'Not engaged', 'Still open', 'Other outcome',
      ];
      const used = new Set();
      links.forEach(l => { used.add(l.source); used.add(l.target); });
      const names = ORDER.filter(n => used.has(n));
      const idx = {};
      names.forEach((n, i) => { idx[n] = i; });
      const nodes = names.map(n => ({ name: n, color: COLORS[n] || '#94A3B8' }));
      const mapped = links
        .filter(l => idx[l.source] !== undefined && idx[l.target] !== undefined && l.value > 0)
        .map(l => ({ source: idx[l.source], target: idx[l.target], value: l.value }));
      if (!mapped.length) return null;
      // FIVE columns, matching the service's four link levels
      // (Eligible -> ADT -> enrol -> reach -> outcome).
      const stageLabels = [
        { label: 'Could have helped', position: 'left' },
        { label: 'Did we know?', position: 0.25 },
        { label: 'Targeted', position: 0.5 },
        { label: 'Outreach', position: 0.75 },
        { label: 'Outcome', position: 'right' },
      ];
      return { nodes, links: mapped, stageLabels };
    },
    sankeyConfig() {
      // width/height are only FALLBACKS — the component measures its container
      // (.sankey-wrapper). Stage labels are drawn at y = -20 (inside the TOP
      // margin), so margin.top must exceed that or they get clipped by the
      // wrapper's overflow:hidden. right margin reserves room for the outcome
      // node labels; left for the cohort label + node text.
      return {
        width: 1500, height: 540,
        margin: { top: 44, right: 160, bottom: 16, left: 92 },
        nodeWidth: 12, nodePadding: 16, iterations: 32, linkOpacity: 0.5,
        formatting: { percentage: false, sig_figs: 0 },
      };
    },
  },
  mounted() {
    this.loadFilterOptions();
    this.fetchData();
    // Close an open column menu on any outside click. Registered on the document
    // because the menu sits inside a scrolling table and a click can land anywhere.
    this._closeMenu = () => { this.openColumnMenu = null; };
    document.addEventListener('click', this._closeMenu);
  },
  beforeUnmount() {
    if (this._closeMenu) document.removeEventListener('click', this._closeMenu);
  },
  methods: {
    pct(n, d) { return d ? Math.round((n / d) * 100) : 0; },
    // Open the episode list for the cohort behind a statistic. `segment` names
    // the same predicate the aggregate used, so the list always reconciles with
    // the number that was clicked.
    drill(segment, label) {
      this.drillSegment = segment;
      this.drillLabel = label;
      this.drillOffset = 0;
      this.selectedEpisode = null;
      this.loadEpisodes();
      // No scrollIntoView: the list is side-by-side and already on screen. Scrolling
      // would move the card the user just clicked out of view.
    },
    // The Sankey replaces the old Reach step panel, so each of its nodes has to
    // drill the same way a card did. Node names come from the service's
    // enrol_node / reach_node / outcome_node CASE expressions.
    onSankeyNode({ name }) {
      const seg = SANKEY_NODE_SEGMENT[name];
      if (!seg) return;
      this.drill(seg, name);
    },
    // A link is the intersection of its two endpoints. Only the pairs that map to
    // a real SEGMENTS predicate are actionable; anything else falls back to the
    // target node, which is always a superset of the ribbon.
    onSankeyLink({ sourceName, targetName }) {
      const seg = SANKEY_LINK_SEGMENT[`${sourceName}|${targetName}`];
      if (seg) { this.drill(seg, `${sourceName} → ${targetName}`); return; }
      const fallback = SANKEY_NODE_SEGMENT[targetName];
      if (fallback) this.drill(fallback, targetName);
    },
    // Clearing a selection returns to the full list rather than emptying it.
    clearDrill() {
      this.drillSegment = 'all';
      this.drillLabel = 'All discharges';
      this.drillOffset = 0;
      this.selectedEpisode = null;
      this.loadEpisodes();
    },
    async loadEpisodes() {
      if (!this.drillSegment) return;
      this.episodesLoading = true;
      try {
        const { data } = await this.$axios.post('/api/care-management/discharge-waterfall/episodes/', {
          segment: this.drillSegment,
          date_from: this.dateFrom, date_to: this.dateTo,
          disposition: this.disposition,
          ...this.cohortParams,
          column_filters: this.activeColumnFilterPayload(),
          limit: this.drillPageSize, offset: this.drillOffset,
        });
        this.episodes = data.rows || [];
        this.episodeTotal = data.total || 0;
      } catch (e) {
        console.error('Failed to load episodes:', e);
        this.episodes = []; this.episodeTotal = 0;
      } finally { this.episodesLoading = false; }
    },
    pageEpisodes(delta) {
      const next = this.drillOffset + delta * this.drillPageSize;
      if (next < 0 || next >= this.episodeTotal) return;
      this.drillOffset = next; this.loadEpisodes();
    },
    openEpisode(row) {
      if (!row.entity_id) return;   // claims-only discharge, never enrolled
      this.selectedEpisode = row;
    },
    fmtDate(d) { return d ? String(d).slice(0, 10) : '—'; },
    ynDash(v) { return v === true ? 'Yes' : (v === false ? 'No' : '—'); },
    loopMark(v) { return v ? '●' : '—'; },
    fmtP(p) {
      if (p === null || p === undefined) return '—';
      if (p < 0.0001) return '< 0.0001';
      return p < 0.001 ? p.toExponential(1) : p.toFixed(4);
    },
    shiftRange(direction) {
      const from = new Date(this.dateFrom + 'T12:00:00'), to = new Date(this.dateTo + 'T12:00:00');
      const span = Math.round((to - from) / 86400000) + 1;
      from.setDate(from.getDate() + direction * span); to.setDate(to.getDate() + direction * span);
      const now = new Date(); now.setHours(23, 59, 59, 999);
      if (to > now) return;
      this.dateFrom = from.toISOString().split('T')[0]; this.dateTo = to.toISOString().split('T')[0];
      this.fetchData();
    },
    applyPreset(preset) { this.dateFrom = preset.from; this.dateTo = preset.to; this.fetchData(); },
    isActivePreset(preset) { return this.dateFrom === preset.from && this.dateTo === preset.to; },
    setDisposition(v) { this.disposition = v; this.fetchData(); },
    onFilterChange() { this.fetchData(); },
    // TAB-separated, because that is what pastes into Excel/Sheets as columns.
    // A header line carries the active cohort so a pasted table is self-describing
    // — a bare grid of numbers with no filter context is how a wrong figure ends up
    // in a deck.
    copyFlowTable() {
      const scope = [
        `${this.dateFrom} to ${this.dateTo}`,
        this.serviceAreas.length ? `Region: ${this.serviceAreas.join('; ')}` : null,
        this.contracts.length ? `Payer: ${this.contracts.join('; ')}` : null,
        this.episodeTypes.length ? `Type: ${this.episodeTypes.join('; ')}` : null,
        this.disposition ? `Disposition: ${this.disposition}` : null,
      ].filter(Boolean).join(' | ');
      const lines = [
        `Discharge flow\t${scope}`,
        'Stage\tFrom\tTo\tDischarges\t% of from',
        ...this.flowTable.map(r => `${r.stage}\t${r.from}\t${r.to}\t${r.value}\t${r.pct}%`),
      ];
      const text = lines.join('\n');
      const done = () => { this.copied = true; setTimeout(() => { this.copied = false; }, 1800); };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(done).catch(() => this.fallbackCopy(text, done));
      } else {
        this.fallbackCopy(text, done);
      }
    },
    // clipboard API needs a secure context; local dev over plain http does not have
    // one, so keep the textarea path rather than failing silently.
    fallbackCopy(text, done) {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); done(); } catch (e) { console.error('Copy failed:', e); }
      document.body.removeChild(ta);
    },
    clearCohortFilters() {
      this.serviceAreas = []; this.contracts = []; this.episodeTypes = [];
      this.fetchData();
    },
    filteredColumnOptions(key) {
      const opts = this.episodeFilterOptions[key] || [];
      const q = this.columnMenuSearch.trim().toLowerCase();
      return q ? opts.filter(o => String(o.value).toLowerCase().includes(q)) : opts;
    },
    toggleColumnMenu(key) {
      this.openColumnMenu = this.openColumnMenu === key ? null : key;
      this.columnMenuSearch = '';
    },
    toggleColumnValue(key, value) {
      const cur = this.columnFilters[key] || [];
      const next = cur.includes(value) ? cur.filter(v => v !== value) : [...cur, value];
      this.columnFilters = { ...this.columnFilters, [key]: next };
      this.drillOffset = 0;
      this.loadEpisodes();
    },
    clearColumnFilter(key) {
      const next = { ...this.columnFilters };
      delete next[key];
      this.columnFilters = next;
      this.openColumnMenu = null;
      this.drillOffset = 0;
      this.loadEpisodes();
    },
    // Only the non-empty filters go over the wire, so the request body stays
    // readable and the backend's truthiness checks behave.
    activeColumnFilterPayload() {
      const out = {};
      Object.entries(this.columnFilters).forEach(([k, v]) => { if (v && v.length) out[k] = v; });
      return Object.keys(out).length ? out : null;
    },
    async loadEpisodeFilterOptions() {
      try {
        const { data } = await this.$axios.post(
          '/api/care-management/discharge-waterfall/episode-filter-options/',
          {
            date_from: this.dateFrom, date_to: this.dateTo, disposition: this.disposition,
            ...this.cohortParams,
          },
        );
        this.episodeFilterOptions = data || {};
      } catch (e) {
        console.error('Failed to load episode filter options:', e);
      }
    },
    async exportEpisodes(format) {
      if (this.exporting) return;
      this.startingExport = true;
      try {
        const job = await startDischargeWaterfallExport({
          segment: this.drillSegment,
          date_from: this.dateFrom, date_to: this.dateTo,
          disposition: this.disposition,
          ...this.cohortParams,
          column_filters: this.activeColumnFilterPayload(),
          format,
        });
        this.exportJobId = job.id;
        this.$store.dispatch('exports/track', job);
      } catch (e) {
        console.error('Export failed to start:', e);
        showErrorToast("Couldn't start the export. Try again.");
      } finally {
        this.startingExport = false;
      }
    },
    async loadFilterOptions() {
      try {
        const { data } = await this.$axios.get('/api/care-management/discharge-waterfall/filter-options/');
        this.filterOptions = data || { service_area: [], contract: [], episode_type: [] };
      } catch (e) {
        console.error('Failed to load filter options:', e);
      }
    },
    async fetchData() {
      this.loading = true;
      try {
        const { data } = await this.$axios.post('/api/care-management/discharge-waterfall/', {
          date_from: this.dateFrom, date_to: this.dateTo, disposition: this.disposition,
          ...this.cohortParams,
        });
        this.result = data;
      } catch (e) {
        console.error('Failed to fetch discharge waterfall:', e);
        this.result = { counts: {}, readmit_histogram: [], barrier_model: null };
      } finally {
        this.loading = false;
      }
      // The episode list is scoped by the same date/disposition filters, so it
      // has to reload whenever they change or it would contradict the panels.
      this.drillOffset = 0;
      this.selectedEpisode = null;
      this.loadEpisodes();
      // Column-filter options are cohort-scoped, so they move with the cohort.
      this.loadEpisodeFilterOptions();
    },
  },
};
</script>

<style scoped>
/* Clickable statistics: any aggregate that drills the episode list. */
.wf-clickable { cursor: pointer; transition: border-color .15s, box-shadow .15s; }
.wf-clickable:hover { border-color: #0284C7 !important; box-shadow: 0 1px 6px rgba(2,132,199,.14); }
.wf-clickable.wf-active { border-color: #0284C7 !important; background: #F0F9FF !important; }
.wf-clickable-row { cursor: pointer; }
.wf-clickable-row:hover { background: #F8FAFC; }
.wf-clickable-row.wf-active { background: #F0F9FF; }

/* Active drill-down selection, shown above the episode list. */
.wf-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 2px 6px 2px 8px;
  border: 1px solid #BAE6FD;
  border-radius: 10px;
  background: #F0F9FF;
  color: #0369A1;
  font-size: 11px;
  font-weight: 600;
}
.wf-chip-x {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: #0369A1;
  cursor: pointer;
}
.wf-chip-x:hover { background: #BAE6FD; }

/* Copy-table button. */
.wf-copy {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 9px;
  border: 1px solid #E8E9E9;
  border-radius: 4px;
  background: #FFFFFF;
  color: #6B7280;
  font-size: 10.5px;
  font-weight: 600;
  cursor: pointer;
}
.wf-copy:hover { border-color: #0284C7; color: #0369A1; }
.wf-copy:disabled { opacity: .5; cursor: default; }

/* Column filter: the header itself is the control. */
.wf-colfilter {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: none;
  background: none;
  padding: 0;
  font: inherit;
  color: inherit;
  cursor: pointer;
  white-space: nowrap;
}
.wf-colfilter:hover { color: #0369A1; }
.wf-colfilter.on { color: #0369A1; }
.wf-colfilter.on span {
  background: #0284C7;
  color: #FFFFFF;
  border-radius: 7px;
  padding: 0 4px;
  font-size: 9px;
}
/* The menu must escape the scroll container's clipping, so it is fixed-width and
   absolutely positioned against the th (which is position:sticky, hence relative). */
.wf-colmenu {
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 20;
  width: 250px;
  background: #FFFFFF;
  border: 1px solid #E8E9E9;
  border-radius: 6px;
  box-shadow: 0 4px 16px rgba(38, 40, 42, .16);
  padding: 6px;
}
.wf-colmenu-search {
  width: 100%;
  border: 1px solid #E8E9E9;
  border-radius: 4px;
  padding: 3px 6px;
  font-size: 11px;
  margin-bottom: 4px;
}
.wf-colmenu-list { max-height: 220px; overflow: auto; }
.wf-colmenu-row {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 3px 4px;
  font-size: 11px;
  font-weight: 400;
  color: #353537;
  cursor: pointer;
  text-transform: none;
  letter-spacing: normal;
}
.wf-colmenu-row:hover { background: #F8FAFC; }
.wf-colmenu-label { flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.wf-colmenu-count { color: #A5A5A6; font-variant-numeric: tabular-nums; }
.wf-colmenu-foot {
  display: flex;
  justify-content: space-between;
  border-top: 1px solid #F0F0F1;
  margin-top: 4px;
  padding-top: 4px;
}
.wf-colmenu-foot button {
  background: none;
  border: none;
  font-size: 10.5px;
  color: #6B7280;
  cursor: pointer;
  text-decoration: underline;
}
.wf-colmenu-foot button:hover { color: #0369A1; }

/* Sankey_Diagram renders position:absolute inset-0 and reads its size from the
   container's clientWidth/clientHeight. It therefore needs a positioned,
   explicitly-sized parent — otherwise it escapes to the nearest positioned
   ancestor and draws over the entire page. */
.sankey-wrapper {
  position: relative;
  width: 100%;
  height: 540px;
  overflow: hidden;
}

/* Episode list. Single column, so the table body scrolls INSIDE the panel and is
   capped — an unbounded 22,450-row list would push the analysis panels below it
   off the page entirely. The header and pager stay put while rows scroll. */
.wf-list-scroll {
  max-height: 460px;
  overflow: auto;
}
.wf-th {
  position: sticky;
  top: 0;
  z-index: 1;
  background: #FFFFFF;
  border-bottom: 1px solid #E8E9E9;
  padding: 6px 8px;
  font-size: 10.5px;
  font-weight: 600;
  color: #6B7280;
  white-space: nowrap;
}
.wf-td {
  padding: 6px 8px;
  font-size: 11.5px;
  color: #353537;
  white-space: nowrap;
}
.wf-td-clip {
  max-width: 130px;
  overflow: hidden;
  text-overflow: ellipsis;
}

</style>
